/**
 * Law OS v2 — Dashboard
 *
 * Modern legal-ops dashboard per design_guidelines.json:
 *   1. Today's Priorities  — Hearings Today/Tomorrow, Urgent Deadlines,
 *      Follow-Ups, Pending Signatures, Pending Documents, High-Priority Tasks
 *   2. Quick Actions       — Add Litigation, Add Client, Create Draft,
 *      Upload Document, Schedule Hearing, Create Task
 *   3. Insights            — Active Cases, Cases by Status, Upcoming Hearings,
 *      Team Productivity, Billing Overview
 *
 * Palette strictly per user-mandated spec (#2D3A8C indigo · #10B981 emerald ·
 * #F59E0B warning · #EF4444 danger · #F8FAFC bg · #FFFFFF card · #0F172A ink).
 * Status colors per spec (Filed blue · Hearing Scheduled purple · Awaiting Client
 * orange · In Progress cyan · Won green · Closed gray).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Briefcase, Users, Gavel, CheckSquare, FileText, FileSignature, Upload,
    Calendar as CalendarIcon, Plus, ArrowRight, AlertTriangle, Clock,
    TrendingUp, Receipt, Sparkles,
} from "lucide-react";

const STATUS_COLOR = {
    filed:    "#3B82F6",
    hearing_scheduled: "#A855F7",
    awaiting_client: "#F97316",
    in_progress: "#06B6D4",
    won: "#10B981",
    closed: "#94A3B8",
};

const STATUS_LABEL = {
    filed: "Filed",
    hearing_scheduled: "Hearing Scheduled",
    awaiting_client: "Awaiting Client",
    in_progress: "In Progress",
    won: "Won",
    closed: "Closed",
};

export default function TenantDashboardV2() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/dashboard/overview");
            setData(r.data);
        } catch { /* swallow — show empty state */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const greet = () => {
        const h = new Date().getHours();
        if (h < 12) return "Good morning";
        if (h < 17) return "Good afternoon";
        return "Good evening";
    };
    const firstName = (user?.name || user?.email || "").split(/[ @]/)[0];

    return (
        <div className="space-y-8" data-testid="dashboard-v2">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-heading text-4xl font-bold text-foreground tracking-tight">{greet()}, {firstName}</h1>
                    <p className="text-sm text-muted-foreground mt-1">Here's what's on your plate today, {new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "long" })}.</p>
                </div>
                <Link to="/app/ai" data-testid="dash-ai-link"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 shadow-lawos-md transition-all">
                    <Sparkles className="w-4 h-4"/> Ask AI
                </Link>
            </div>

            {/* TODAY'S PRIORITIES */}
            <section data-testid="todays-priorities">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground">Today's Priorities</h2>
                    {data?.priorities_count > 0 && <span className="text-xs text-muted-foreground">{data.priorities_count} items need attention</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <PriorityCard icon={Gavel} label="Hearings today" value={data?.hearings_today ?? 0} tone="indigo"   href="/app/cases?next_hearing=today"      testId="prio-hearings-today"/>
                    <PriorityCard icon={Gavel} label="Hearings tomorrow" value={data?.hearings_tomorrow ?? 0} tone="indigo" href="/app/cases?next_hearing=tomorrow" testId="prio-hearings-tomorrow"/>
                    <PriorityCard icon={AlertTriangle} label="Urgent deadlines" value={data?.urgent_deadlines ?? 0} tone="danger"  href="/app/tasks?filter=urgent"     testId="prio-urgent-deadlines"/>
                    <PriorityCard icon={Users} label="Client follow-ups" value={data?.client_followups ?? 0} tone="warning" href="/app/communications?tab=followups" testId="prio-followups"/>
                    <PriorityCard icon={FileSignature} label="Pending signatures" value={data?.pending_signatures ?? 0} tone="warning" href="/app/documents?status=pending_sig" testId="prio-pending-sig"/>
                    <PriorityCard icon={FileText} label="Pending documents" value={data?.pending_documents ?? 0} tone="muted" href="/app/documents?status=pending"      testId="prio-pending-docs"/>
                    <PriorityCard icon={CheckSquare} label="High-priority tasks" value={data?.high_priority_tasks ?? 0} tone="indigo" href="/app/tasks?priority=high"       testId="prio-high-tasks"/>
                    <PriorityCard icon={Clock} label="Overdue items" value={data?.overdue_items ?? 0} tone="danger"  href="/app/tasks?filter=overdue"    testId="prio-overdue"/>
                </div>
            </section>

            {/* QUICK ACTIONS */}
            <section data-testid="quick-actions">
                <h2 className="font-heading text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-4">Quick Actions</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <QuickAction icon={Briefcase}     label="Add litigation"        href="/app/cases?new=1"     testId="qa-add-case"/>
                    <QuickAction icon={Users}         label="Add client"      href="/app/clients?new=1"   testId="qa-add-client"/>
                    <QuickAction icon={FileSignature} label="Create draft"    href="/app/ai-docs?new=1"   testId="qa-create-draft"/>
                    <QuickAction icon={Upload}        label="Upload document" href="/app/documents?new=1" testId="qa-upload-doc"/>
                    <QuickAction icon={CalendarIcon}  label="Schedule hearing" href="/app/hearings?new=1" testId="qa-schedule-hearing"/>
                    <QuickAction icon={CheckSquare}   label="Create task"     href="/app/tasks?new=1"     testId="qa-create-task"/>
                </div>
            </section>

            {/* INSIGHTS */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="insights">
                <StatCard icon={Briefcase} label="Active litigations"    value={data?.active_cases ?? 0}    href="/app/cases?status=active"     accent="indigo"/>
                <StatCard icon={Gavel}     label="Upcoming hearings (7d)" value={data?.upcoming_hearings ?? 0} href="/app/cases?next_hearing=upcoming"  accent="indigo"/>
                <StatCard icon={Receipt}   label="Outstanding ₹"   value={`₹${Number(data?.outstanding_inr || 0).toLocaleString("en-IN")}`} href="/app/invoices?status=outstanding" accent="warning"/>

                <CasesByStatusCard counts={data?.cases_by_status || {}}/>

                <div className="rounded-2xl bg-card border border-border p-5 shadow-lawos" data-testid="team-productivity-card">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-primary"/>
                        <div className="text-sm font-medium">Team productivity</div>
                    </div>
                    {data?.team_productivity?.length ? (
                        <ul className="space-y-2">
                            {data.team_productivity.slice(0, 5).map((m) => (
                                <li key={m.user_id} className="flex items-center justify-between text-sm">
                                    <span className="text-foreground truncate">{m.name}</span>
                                    <span className="text-muted-foreground text-xs">{m.completed_tasks_7d ?? 0} tasks · {m.hours_logged_7d ?? 0}h</span>
                                </li>
                            ))}
                        </ul>
                    ) : <p className="text-xs text-muted-foreground">No productivity data yet this week.</p>}
                </div>

                <div className="rounded-2xl bg-card border border-border p-5 shadow-lawos" data-testid="billing-overview-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Receipt className="w-4 h-4 text-primary"/>
                        <div className="text-sm font-medium">Billing — last 30 days</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        <BillingStat label="Invoiced" value={data?.billing_30d?.invoiced ?? 0}/>
                        <BillingStat label="Collected" value={data?.billing_30d?.collected ?? 0} tone="emerald"/>
                        <BillingStat label="Pending"  value={data?.billing_30d?.pending ?? 0} tone="warning"/>
                    </div>
                    <Link to="/app/invoices" className="inline-flex items-center gap-1 mt-4 text-xs text-primary hover:underline">
                        View all invoices <ArrowRight className="w-3 h-3"/>
                    </Link>
                </div>
            </section>
        </div>
    );
}

// =====================================================================
function PriorityCard({ icon: Icon, label, value, tone, href, testId }) {
    const toneCls = {
        indigo:  "text-primary",
        emerald: "text-[#10B981]",
        warning: "text-[#F59E0B]",
        danger:  "text-[#EF4444]",
        muted:   "text-muted-foreground",
    }[tone] || "text-primary";
    const ring = value > 0 ? "ring-1 ring-primary/20" : "";
    return (
        <Link to={href} data-testid={testId}
            className={`group rounded-2xl bg-card border border-border p-4 shadow-lawos-sm hover:shadow-lawos-md hover:-translate-y-0.5 transition-all ${ring}`}>
            <div className="flex items-start justify-between">
                <div>
                    <div className={`text-3xl font-bold ${value > 0 ? toneCls : "text-muted-foreground/50"}`}>{value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{label}</div>
                </div>
                <Icon className={`w-5 h-5 ${toneCls} opacity-70 group-hover:opacity-100`}/>
            </div>
        </Link>
    );
}

function QuickAction({ icon: Icon, label, href, testId }) {
    return (
        <Link to={href} data-testid={testId}
            className="group rounded-xl bg-card border border-border p-4 shadow-lawos-sm hover:shadow-lawos-md hover:border-primary/40 hover:-translate-y-0.5 transition-all flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                <Icon className="w-4 h-4 text-primary" strokeWidth={2}/>
            </div>
            <div className="text-sm font-medium text-foreground">{label}</div>
            <Plus className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors ml-auto -mt-7"/>
        </Link>
    );
}

function StatCard({ icon: Icon, label, value, href, accent }) {
    const accentCls = {
        indigo: "text-primary",
        warning: "text-[#F59E0B]",
        emerald: "text-[#10B981]",
    }[accent] || "text-primary";
    return (
        <Link to={href} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
            className="rounded-2xl bg-card border border-border p-5 shadow-lawos hover:shadow-lawos-md transition-all flex items-center justify-between">
            <div>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className={`text-2xl font-bold mt-1 ${accentCls}`}>{value}</div>
            </div>
            <Icon className={`w-5 h-5 ${accentCls} opacity-50`}/>
        </Link>
    );
}

function CasesByStatusCard({ counts }) {
    const entries = Object.entries(counts);
    const total = entries.reduce((a, [, n]) => a + n, 0);
    const max = entries.reduce((m, [, n]) => Math.max(m, n), 0) || 1;
    return (
        <div className="rounded-2xl bg-card border border-border p-5 shadow-lawos" data-testid="cases-by-status-card">
            <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-medium">Litigations by status</div>
                <span className="text-xs text-muted-foreground">{total} total</span>
            </div>
            {total === 0 ? (
                <p className="text-xs text-muted-foreground">No litigations yet.</p>
            ) : (
                <div className="space-y-3" data-testid="cases-by-status-bars">
                    {/* Vertical bar chart — each column: count · bar */}
                    <div className="flex items-end gap-2 h-32 px-1 border-b border-border">
                        {entries.map(([k, n]) => {
                            // Chart is 128 px (h-32). Reserve ~18 px for count label + gap.
                            const barPx = Math.max(4, Math.round((n / max) * 110));
                            return (
                                <Link to={`/app/cases?status=${k}`} key={k}
                                      data-testid={`cases-bar-${k}`}
                                      title={`${STATUS_LABEL[k] || k}: ${n}`}
                                      className="flex-1 h-full flex flex-col items-center justify-end gap-1 group cursor-pointer">
                                    <span className="text-[10px] font-mono text-muted-foreground group-hover:text-foreground">{n}</span>
                                    <div className="w-full rounded-t transition-all group-hover:opacity-80"
                                         style={{
                                             height: `${barPx}px`,
                                             background: STATUS_COLOR[k] || "#94A3B8",
                                         }}/>
                                </Link>
                            );
                        })}
                    </div>
                    {/* X-axis labels */}
                    <div className="flex items-start gap-2 px-1">
                        {entries.map(([k]) => (
                            <div key={k} className="flex-1 text-center">
                                <div className="text-[9px] uppercase tracking-wider text-muted-foreground leading-tight">
                                    {(STATUS_LABEL[k] || k).split(" ").map((w, i) => <div key={i}>{w}</div>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function BillingStat({ label, value, tone }) {
    const toneCls = {
        emerald: "text-[#10B981]",
        warning: "text-[#F59E0B]",
    }[tone] || "text-foreground";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-base font-semibold mt-0.5 ${toneCls}`}>₹{Number(value || 0).toLocaleString("en-IN")}</div>
        </div>
    );
}
